import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiJson } from '@/lib/apiClient';
import { normalizeVehicleType } from '../vehicles/useVehicleDetail';
import type { LiveResponse, LiveVehicle } from './types';
import { normalizeTrackerHealth } from './trackerHealth';
import { normalizeVehicleState } from './vehicleState';
import { readPersistedLive } from './livePersist';

/**
 * Client-side view of `GET /api/vehicles/live` — now the FALLBACK, not the
 * primary transport (seam S7). Live data arrives over the WebSocket
 * (`useLiveSocket`), which applies frames into THIS query's cache. The poll is
 * demoted to a slow reconcile, and it is DISABLED entirely while the socket is
 * healthy:
 *
 *   - `socketHealthy: true` (socket open) → `refetchInterval: false`. No poll
 *     fires; every position comes over the wire. This is what makes AC-4
 *     provable — the marker moves with no `/api/vehicles/live` request.
 *   - `socketHealthy: false` (connecting, reconnecting, or unsupported) →
 *     `refetchInterval: 60_000`. A slow floor so a silent socket outage cannot
 *     freeze the map — slice 07's lesson that a silent failure survives every
 *     internal probe. 60 s, not the old 15 s: the socket is the fresh path now,
 *     so the fallback only needs to be a safety net.
 *   - `refetchIntervalInBackground: false` stays — a BACKGROUNDED map must cost
 *     ZERO bytes on a metered connection. This is load-bearing.
 *
 * The list of vehicles always comes from our own DB, so a liveness outage costs
 * the live fields only (`degraded: true`) — never the list itself.
 */

/** A wire heading softened to `number | null`: a finite 0–360 stays, anything else
 *  (absent, null, NaN, non-number) becomes null. Guards the S3-W `null` invariant. */
function courseOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export const liveVehiclesQueryKey = ['vehicles', 'live'] as const;

/** The demoted fallback interval — a reconcile, not the primary filler. */
export const LIVE_FALLBACK_REFETCH_MS = 60_000;

export interface UseLiveVehiclesOptions {
  /** True while the WebSocket is open — suppresses the fallback poll entirely. */
  socketHealthy?: boolean;
  /** The authenticated principal (tenant id) whose on-device last-known snapshot
   *  may seed the query. Omitted → no seed is read, so nothing can leak across a
   *  principal boundary. See livePersist §Tenant isolation. */
  tenantId?: string;
}

export function useLiveVehicles(
  { socketHealthy = false, tenantId }: UseLiveVehiclesOptions = {},
): UseQueryResult<LiveResponse> {
  // Seed from the device's last-known snapshot (livePersist) so a COLD launch —
  // especially offline, on a phone that just opened the installed app in a dead
  // zone — paints the fleet's last positions immediately instead of a blank
  // error. `initialDataUpdatedAt: savedAt` is deliberately in the PAST, so online
  // the query is stale on arrival and refetches at once, replacing the seed with
  // live data. Until that lands (or while offline) the map renders the seed in its
  // explicit "last-known" mode — absolute times, past-tense markers — never as
  // live (LiveMap: `lastKnown`). `dataUpdatedAt === savedAt` is exactly how LiveMap
  // detects "still showing the seed".
  const seed = tenantId ? readPersistedLive(tenantId) : null;

  return useQuery({
    queryKey: liveVehiclesQueryKey,
    ...(seed ? { initialData: seed.data, initialDataUpdatedAt: seed.savedAt } : {}),
    queryFn: async ({ signal }) => {
      const res = await apiJson<LiveResponse>('/api/vehicles/live', { signal });
      // The wire carries `vehicleType: VehicleType | null` on each item
      // (api VehicleLiveItem). Normalise here so an absent/unknown/null value
      // becomes `other` (→ the dot) and the marker code can treat it as required,
      // never guarding for null and never throwing a missing-glyph on a customer's
      // screen. This is the ONE place the raw cast gains a transform.
      return {
        ...res,
        // Same defensive guard as useVehicles (13e): normalising at the boundary
        // must not be able to throw. The gate only caught the list hook because its
        // stub happens to return a well-formed body here — an absence of evidence,
        // not of the defect.
        vehicles: (Array.isArray(res?.vehicles) ? res.vehicles : []).map((v): LiveVehicle => ({
          ...v,
          vehicleType: normalizeVehicleType((v as { vehicleType?: unknown }).vehicleType),
          // Seam S1 (13e). Absent/unrecognised → `unknown`: the honest default for a
          // vehicle we cannot characterise, and what the API emits when ignition is
          // unknown. Never guessed from `motion`.
          state: normalizeVehicleState((v as { state?: unknown }).state),
          // Seam S3-W. Absent (wire not shipped yet), null, or non-finite → null:
          // "no moving fix ever observed" → the marker draws the DOT, never a due
          // north silhouette. NEVER defaulted to 0 — 0 is a real heading.
          lastMovingCourse: courseOrNull((v as { lastMovingCourse?: unknown }).lastMovingCourse),
          // Seam S1 — tracker health. Preserves the null-vs-present distinction:
          // absent → null (no telemetry), object → object with per-field nulls
          // (reporting, possibly no readings). Never fabricated from a null.
          trackerHealth: normalizeTrackerHealth((v as { trackerHealth?: unknown }).trackerHealth),
          // 18g — same rule as the list hook, and for the same reason: absent
          // means `true`. Defaulting to `false` would label an entire fleet "No
          // tracker" the moment a field went missing.
          hasTracker: (v as { hasTracker?: unknown }).hasTracker !== false,
          // 13f seam S5 — the open-offline `started_at`, or null when reporting.
          // Absent → null, matching the list hook.
          trackerSilentSince:
            (v as { trackerSilentSince?: string | null }).trackerSilentSince ?? null,
        })),
      };
    },
    // The socket is the fresh path; poll only as a fallback, and never while the
    // socket is healthy.
    refetchInterval: socketHealthy ? false : LIVE_FALLBACK_REFETCH_MS,
    refetchIntervalInBackground: false,
    // Focus/reconnect refreshes are owned by the socket (it re-establishes and
    // refetches on focus and on recovery), so the query does not double up.
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
}
